import type {
  ChecklistEntry,
  ChecklistParallelDefinition,
  ChecklistSourceSelection,
} from '@tcvs/shared';
import {
  applicableParallelDefinitions,
  checklistEntryPolicy,
  checklistPlainChoiceAllowed,
  checklistSelectionSource,
  composeReviewedChecklistIdentity,
} from './checklist-parallel-selection';

/** Request/visit association supplied by the owner of the currently loaded options. */
export interface ChecklistVariantAssociation {
  cardId: string;
  visitId: string;
  requestId: string;
  checklistId: string;
  printedNumber: string;
}
export interface ChecklistVariantWarningProps {
  entry?: ChecklistEntry;
  definitions?: readonly ChecklistParallelDefinition[];
  selectedSource?: ChecklistSourceSelection | null;
  current: ChecklistVariantAssociation;
  loaded?: ChecklistVariantAssociation;
  loading?: boolean;
}
export interface ChecklistVariantChoice {
  key: string;
  label: string;
  definitionId?: string;
}
export interface ChecklistVariantWarningModel {
  currentChoice: ChecklistVariantChoice;
  choices: readonly ChecklistVariantChoice[];
  beforeBase: boolean;
}
const exact = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (object(value))
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`)
      .join(',')}}`;
  return JSON.stringify(value);
}
function sameSource(actual: ChecklistSourceSelection, expected: ChecklistSourceSelection): boolean {
  for (const field of [
    'checklistId',
    'sourceSystem',
    'sourceTargetId',
    'sourceItemId',
    'sourceSubsetId',
    'sourceDefinitionType',
    'sourceFamilyId',
    'snapshotFingerprint',
    'subset',
  ] as const)
    if (!exact(actual[field]) || exact(actual[field]) !== exact(expected[field])) return false;
  return (
    canonical(actual.naturalKey) === canonical(expected.naturalKey) &&
    canonical(actual.parallel) === canonical(expected.parallel)
  );
}
function currentAssociation(input: ChecklistVariantWarningProps): boolean {
  const fields = ['cardId', 'visitId', 'requestId', 'checklistId', 'printedNumber'] as const;
  return (
    !input.loading &&
    !!input.loaded &&
    fields.every(
      (field) =>
        typeof input.current?.[field] === 'string' &&
        input.current[field] !== '' &&
        input.current[field] === input.loaded?.[field],
    )
  );
}
/** Pure display advice for one exact loaded row; never selection/save authority. */
export function checklistVariantWarningModel(
  input: ChecklistVariantWarningProps,
): ChecklistVariantWarningModel | undefined {
  const { entry, definitions, selectedSource } = input;
  if (!currentAssociation(input) || !entry || !definitions || !selectedSource) return undefined;
  const policy = checklistEntryPolicy(entry),
    source = entry.source;
  if (
    !policy ||
    !source ||
    entry.unresolvedClassification ||
    source.sourceSystem !== 'azure-sql-ebay' ||
    input.current.checklistId !== `SOURCE#${source.sourceSystem}#${source.sourceTargetId}` ||
    exact(input.current.printedNumber) !== exact(entry.identity.number) ||
    exact(entry.key.number) !== exact(entry.identity.number) ||
    exact(policy.subsetDefinition?.source.snapshotFingerprint) !== exact(source.snapshotFingerprint)
  )
    return undefined;
  const qualified = new Map<string, ChecklistParallelDefinition>();
  for (const definition of definitions) {
    // The existing composer owns applicability. This only establishes the loaded revision/product association.
    if (
      !object(definition) ||
      !object(definition.source) ||
      !object(definition.applicability) ||
      !exact(definition.definitionId) ||
      !exact(definition.name) ||
      definition.source.system !== source.sourceSystem ||
      definition.source.targetId !== source.sourceTargetId ||
      definition.source.snapshotFingerprint !== source.snapshotFingerprint ||
      !exact(definition.source.subsetId) ||
      !exact(definition.source.familyId)
    )
      continue;
    const key = JSON.stringify([
      source.sourceSystem,
      source.sourceTargetId,
      definition.definitionId,
    ]);
    const prior = qualified.get(key);
    if (prior && canonical(prior) !== canonical(definition)) return undefined;
    qualified.set(key, definition);
  }
  const eligible = applicableParallelDefinitions(entry, [...qualified.values()]);
  const baseAllowed =
    checklistPlainChoiceAllowed(entry) && composeReviewedChecklistIdentity(entry).ok;
  const selectedDefinition = selectedSource.parallel
    ? eligible.find(
        (definition) => definition.definitionId === selectedSource.parallel?.definitionId,
      )
    : undefined;
  if (selectedSource.parallel && !selectedDefinition) return undefined;
  if (!selectedDefinition && policy.rowTreatmentStatus === 'PLAIN' && !baseAllowed)
    return undefined;
  if (!composeReviewedChecklistIdentity(entry, selectedDefinition).ok) return undefined;
  const expected = checklistSelectionSource(input.current.checklistId, entry, selectedDefinition);
  if (!expected || !sameSource(selectedSource, expected)) return undefined;
  const definitionChoice = (definition: ChecklistParallelDefinition): ChecklistVariantChoice => ({
    key: JSON.stringify([
      source.sourceSystem,
      source.sourceTargetId,
      'definition',
      definition.definitionId,
    ]),
    label: definition.name,
    definitionId: definition.definitionId,
  });
  const base: ChecklistVariantChoice = {
    key: JSON.stringify([source.sourceSystem, source.sourceTargetId, 'plain']),
    label: 'Base (no parallel)',
  };
  const choices = [...(baseAllowed ? [base] : []), ...eligible.map(definitionChoice)];
  const currentChoice = selectedDefinition
    ? definitionChoice(selectedDefinition)
    : policy.rowTreatmentStatus === 'INTRINSIC'
      ? choices.find((choice) => choice.definitionId === policy.intrinsicTreatment?.definitionId)
      : base;
  if (
    !currentChoice ||
    !choices.some((choice) => choice.key === currentChoice.key) ||
    !choices.some((choice) => choice.key !== currentChoice.key)
  )
    return undefined;
  return { currentChoice, choices, beforeBase: currentChoice.key === base.key };
}

/** Inert warning; native expansion reveals the complete supplied eligible label list. */
export function ChecklistVariantWarning(props: ChecklistVariantWarningProps) {
  const model = checklistVariantWarningModel(props);
  if (!model) return null;
  const repeatedLabels = new Set(
    model.choices
      .filter(
        (choice, index, all) => all.findIndex((other) => other.label === choice.label) !== index,
      )
      .map((choice) => choice.label),
  );
  return (
    <aside role="note" aria-label="Catalog alternate versions">
      <p>
        <strong>Catalog lists alternate versions for this printed number.</strong>{' '}
        {model.beforeBase
          ? 'Inspect the image and treatment before choosing Base.'
          : 'Inspect the image and treatment before changing the selected version.'}
      </p>
      <p>Current catalog choice: {model.currentChoice.label}.</p>
      <details>
        <summary>Show eligible versions in these catalog results ({model.choices.length})</summary>
        <ul style={{ maxHeight: '12rem', overflowY: 'auto', overflowWrap: 'anywhere' }}>
          {model.choices.map((choice) => (
            <li key={choice.key}>
              {choice.label}
              {choice.key === model.currentChoice.key ? ' · Current choice' : ''}
              {repeatedLabels.has(choice.label) && choice.definitionId
                ? ` · Reference: ${choice.definitionId}`
                : ''}
            </li>
          ))}
        </ul>
      </details>
    </aside>
  );
}
